import { useState } from "react";
import { motion } from "framer-motion";
import { Rocket, Compass, Target, TrendingUp, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import EnquiryDialog from "@/components/EnquiryDialog";
import SectionHeading from "@/components/SectionHeading";
import { Link } from "react-router-dom";

const STEPS = [
  {
    n: "01",
    icon: Compass,
    title: "Business Assessment",
    desc: "We understand your route, target segment (airport, city, tour), operating hours and capital.",
  },
  {
    n: "02",
    icon: Target,
    title: "Vehicle Match",
    desc: "We recommend the exact CNG / EV model matched to your route economics.",
  },
  {
    n: "03",
    icon: Rocket,
    title: "Finance & Papers",
    desc: "Loan planning, T-Permit registration, insurance and RTO — handled end-to-end.",
  },
  {
    n: "04",
    icon: TrendingUp,
    title: "Go Live & Scale",
    desc: "Attachment guidance, driver onboarding and unit-economics support to help you scale.",
  },
];

const HELPS = [
  "Vehicle Selection matched to route",
  "Finance Assistance & Bank Coordination",
  "Documentation Support",
  "T-Permit & RTO Guidance",
  "Operational Advice",
  "Fleet Scaling Consultation",
];

export default function BusinessOpportunity() {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="business-page">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-40 right-1/4 w-[800px] h-[500px] radial-glow-red pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
            <Rocket className="w-4 h-4" /> Business Opportunity
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
            Become a <span className="text-gradient-red">Commercial Taxi Owner</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-3xl leading-relaxed">
            Whether you're starting with your first commercial vehicle or expanding an
            existing fleet, Be Easy Cars guides you at every stage — from selecting the
            right vehicle to going live with operations.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={() => setOpen(true)}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-8 h-13"
              data-testid="business-consult-btn"
            >
              Book Free Consultation
            </Button>
            <Link to="/finance" data-testid="business-emi-btn">
              <Button
                size="lg"
                variant="outline"
                className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full"
              >
                Check EMI
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Steps bento */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="How It Works" title="A structured path from enquiry to your first ride" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass glass-hover rounded-3xl p-7 relative overflow-hidden"
                data-testid={`business-step-${i}`}
              >
                <div className="absolute top-4 right-4 font-display font-black text-6xl text-white/5">{s.n}</div>
                <s.icon className="w-7 h-7 text-red-500 mb-5" />
                <h3 className="font-display font-bold text-xl text-white">{s.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What we help with */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading eyebrow="What we help with" title="Full-stack setup for taxi entrepreneurs" />
            <ul className="space-y-3">
              {HELPS.map((h) => (
                <li key={h} className="flex items-start gap-3 text-slate-200 text-base">
                  <CheckCircle2 className="w-5 h-5 text-red-500 mt-0.5" /> {h}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl overflow-hidden aspect-[4/3]">
            <img
              src="https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=1600&q=80"
              alt="Fleet"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Earning potential (compliant) */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="glass rounded-3xl p-8 md:p-10 border-l-4 border-red-500">
            <div className="text-xs uppercase tracking-[0.28em] text-red-500 font-mono-brand mb-3">
              Earning Potential — Illustrative
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
              Understand the unit economics honestly
            </h3>
            <p className="text-slate-300 leading-relaxed">
              With the right operations and vehicle utilisation, commercial taxi
              owners can build a sustainable transport business. Depending on the
              vehicle type, operating hours, routes and expenses, some operators
              generate gross monthly revenue in the range of{" "}
              <span className="text-white font-semibold">₹90,000 – ₹1,00,000</span>{" "}
              per vehicle. Actual income and profitability vary based on business
              performance, fuel costs, maintenance and market conditions. This is
              an illustrative figure, not a guarantee.
            </p>
            <Button
              onClick={() => setOpen(true)}
              className="mt-6 bg-red-600 hover:bg-red-700 rounded-full"
              data-testid="business-plan-btn"
            >
              Discuss My Business Plan <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <EnquiryDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
